import React, { useState } from 'react';
import ToggleButtons from '../../components/Components/Buttons/ToggleButtons';
import { formatFullDomainUrl } from '../../services/utils/domainUtils';

// Funzione per ottenere l'URL dell'immagine in modo sicuro
const getSafeImageUrl = (imageObj) => {
    if (!imageObj) return '';

    try {
        // Se è un oggetto File diretto
        if (imageObj instanceof File) {
            return URL.createObjectURL(imageObj);
        }

        // Se è un oggetto upload di Ant Design con originFileObj
        if (imageObj.originFileObj instanceof File) {
            return URL.createObjectURL(imageObj.originFileObj);
        }

        // Se ha un URL diretto come stringa
        if (typeof imageObj.url === 'string' && imageObj.url) {
            return imageObj.url;
        }

        // Se è una stringa URL
        if (typeof imageObj === 'string') {
            return imageObj;
        }

        // Se ha un thumbUrl come stringa
        if (typeof imageObj.thumbUrl === 'string' && imageObj.thumbUrl) {
            return imageObj.thumbUrl;
        }
    } catch (error) {
        console.error('Error getting image URL:', error);
        return '';
    }

    return '';
};

// Componente per il colore del prodotto
const ColorOption = ({ color }) => (
    <div
        style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: color,
            display: 'inline-block',
            margin: '0 2px',
            border: '1px solid #e0e0e0'
        }}
    />
);

// Componente per la card del prodotto
const ProductCard = ({ product, customization }) => {
    return (
        <div style={{
            border: '1px solid #eee',
            borderRadius: `${customization?.cardCornerTopLeft || 0}px ${customization?.cardCornerTopRight || 0}px ${customization?.cardCornerBottomRight || 0}px ${customization?.cardCornerBottomLeft || 0}px`,
            overflow: 'hidden',
            backgroundColor: '#FFFFFF', // Sempre bianco
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
            <div style={{ padding: '0 15px', textAlign: 'center', position: 'relative' }}>
                {/* Colori in alto a destra */}
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    display: 'flex'
                }}>
                    {product.colors.map((color, index) => (
                        <ColorOption key={index} color={color} />
                    ))}
                </div>
                <div style={{ paddingTop: '20px' }}>
                    <img
                        src={product.image}
                        alt={product.name}
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            maxHeight: '120px',
                            marginTop: '16px',
                            marginBottom: '-10px'
                        }}
                    />
                </div>
            </div>
            <div style={{
                padding: '10px 15px 15px 15px',
                backgroundColor: '#FFFFFF' // Sempre bianco
            }}>
                <div style={{
                    textAlign: 'center',
                    marginBottom: '10px'
                }}>
                    <strong style={{
                        color: '#333333',
                        fontSize: '14px',
                        fontWeight: '600'
                    }}>{product.name}</strong>
                </div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px',
                    marginTop: '10px'
                }}>
                    <button style={{
                        flex: 1,
                        backgroundColor: typeof customization?.primaryColor === 'object' && customization?.primaryColor?.toHexString ?
                            customization.primaryColor.toHexString() :
                            (customization?.primaryColor || '#17365D'),
                        border: 'none',
                        color: 'white',
                        borderRadius: `${customization?.buttonCornerTopLeft || 0}px ${customization?.buttonCornerTopRight || 0}px ${customization?.buttonCornerBottomRight || 0}px ${customization?.buttonCornerBottomLeft || 0}px`,
                        padding: '10px 0',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        textTransform: 'uppercase'
                    }}>VTO</button>
                    <button style={{
                        flex: 1,
                        backgroundColor: typeof customization?.secondaryColor === 'object' && customization?.secondaryColor?.toHexString ?
                            customization.secondaryColor.toHexString() :
                            (customization?.secondaryColor || '#5B9BD5'),
                        border: 'none',
                        color: 'white',
                        borderRadius: `${customization?.buttonCornerTopLeft || 0}px ${customization?.buttonCornerTopRight || 0}px ${customization?.buttonCornerBottomRight || 0}px ${customization?.buttonCornerBottomLeft || 0}px`,
                        padding: '10px 0',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        textTransform: 'uppercase'
                    }}>3D</button>
                </div>
            </div>
        </div>
    );
};

const CatalogPreview = ({ formData }) => {

    // Funzione per garantire che un colore sia una stringa hex valida
    const ensureHexColor = (color, defaultColor) => {
        if (!color) return defaultColor;

        // Se è già una stringa hex valida, usala direttamente
        if (typeof color === 'string' && color.startsWith('#')) {
            return color;
        }

        // Se è un oggetto ColorPicker di Ant Design con toHexString
        if (color && typeof color === 'object' && color.toHexString && typeof color.toHexString === 'function') {
            return color.toHexString();
        }

        // Se è un oggetto con proprietà hex
        if (color && typeof color === 'object' && color.hex) {
            return color.hex;
        }

        return defaultColor;
    };

    // Normalizza i colori per garantire che siano stringhe hex
    const primaryColor = ensureHexColor(formData?.customization?.primaryColor, '#46518a');
    const secondaryColor = ensureHexColor(formData?.customization?.secondaryColor, '#929ACC');
    const backgroundColor = ensureHexColor(formData?.customization?.backgroundColor, '#FFFFFF');
    const actionButtonColor = ensureHexColor(formData?.customization?.actionButtonColor, '#000000');
    const bannerBackgroundColor = ensureHexColor(formData?.customization?.bannerBackgroundColor, '#FFFFFF');

    const [viewMode, setViewMode] = useState('desktop'); // 'desktop' o 'mobile'

    // Dati di esempio per l'anteprima
    const glassesImage = 'https://firebasestorage.googleapis.com/v0/b/arshades-7e18a.appspot.com/o/glasses_data%2FCA8062%2FCS_CPAtQ8ZnkM8mbvwbifuu%2F394?alt=media&token=c8c9f033-8a17-47dd-8efd-0d11d75a7fc7';
    const sampleProducts = [
        { id: 1, name: 'Variant 1', colors: ['#C19A6B', '#333333', '#666666'], image: glassesImage },
        { id: 2, name: 'Variant 2', colors: ['#F5CBA7', '#C19A6B', '#333333'], image: glassesImage },
        { id: 3, name: 'Variant 3', colors: ['#8B4513', '#333333', '#F5CBA7'], image: glassesImage },
        { id: 4, name: 'Variant 4', colors: ['#666666', '#F5CBA7', '#C19A6B'], image: glassesImage }
    ];

    return (
        <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%'
        }}>
            {/* Controlli per la visualizzazione responsive */}
            <div style={{
                position: 'absolute',
                top: '5.5rem'
            }}>
                <ToggleButtons
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                        {
                            value: 'desktop',
                            label: 'Desktop'
                        },
                        {
                            value: 'mobile',
                            label: 'Mobile'
                        }
                    ]}
                    type="button"
                />
            </div>

            {/* Container che simula uno schermo */}
            <div style={{
                width: viewMode === 'desktop' ? '100%' : '375px',
                maxWidth: '100%',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                overflow: 'visible',
                transition: 'all 0.3s ease',
                backgroundColor: '#f5f5f5',
                margin: '0 auto',
                marginTop: '0.5rem'
            }}>
                {/* Barra del browser simulata */}
                <div style={{
                    backgroundColor: '#f0f0f0',
                    padding: '10px',
                    borderBottom: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '6px',
                        marginRight: '10px'
                    }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ff5f57' }}></div>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ffbd2e' }}></div>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#28c940' }}></div>
                    </div>
                    <div style={{
                        flex: 1,
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        {formData.domain ? formatFullDomainUrl(formData.domain) : 'https://example.com/catalog'}
                    </div>
                </div>

                {/* Contenuto del catalogo */}
                <div style={{
                    backgroundColor: formData.customization?.backgroundColor || '#FFFFFF',
                    padding: '0',
                    overflow: 'visible',
                    height: 'auto'
                }}>
                    {/* Banner del catalogo */}
                    <div style={{
                        position: 'relative', // Needed for absolute positioning of background image
                        backgroundColor: formData.banner?.bannerImage ? 'transparent' : bannerBackgroundColor,
                        padding: viewMode === 'desktop' ? '20px' : '10px',
                        height: viewMode === 'desktop' ? `${formData.banner?.height || 100}px` : `${formData.banner?.heightMobile || 100}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: formData.banner?.logoPosition || 'center',
                        overflow: 'hidden', // Ensures content respects border radius
                        borderRadius: `${formData.customization?.bannerCornerTopLeft || 0}px ${formData.customization?.bannerCornerTopRight || 0}px ${formData.customization?.bannerCornerBottomRight || 0}px ${formData.customization?.bannerCornerBottomLeft || 0}px`,
                        transition: 'height 0.3s ease-in-out, border-radius 0.3s ease-in-out', // Smooth transition for height and radius changes
                        marginLeft: viewMode === 'desktop' ? '16px' : '10px',
                        marginRight: viewMode === 'desktop' ? '16px' : '10px'
                    }}>
                        {/* Banner Background Image - NUOVA LOGICA CON PROPRIETÀ SEPARATE */}
                        {(() => {
                            // Determina quale immagine usare in base alla vista
                            const desktopImage = formData.banner?.bannerImage;
                            const mobileImage = formData.banner?.bannerImageMobile;

                            // Seleziona l'immagine in base alla vista o usa il fallback
                            let imageToShow = null;

                            if (viewMode === 'desktop') {
                                // In desktop view, use desktop image or fall back to mobile
                                imageToShow = desktopImage || mobileImage;
                            } else {
                                // In mobile view, use mobile image or fall back to desktop
                                imageToShow = mobileImage || desktopImage;
                            }

                            // Se non c'è nessuna immagine, non mostrare nulla
                            if (!imageToShow) {
                                return null;
                            }

                            // Funzione per ottenere l'URL dell'immagine
                            const getImageUrl = (img) => {
                                if (!img) return '';

                                // Se è un oggetto File diretto
                                if (img instanceof File) {
                                    return URL.createObjectURL(img);
                                }

                                // Se è un oggetto upload di Ant Design con originFileObj
                                if (img.originFileObj instanceof File) {
                                    return URL.createObjectURL(img.originFileObj);
                                }

                                // Se ha un URL diretto
                                if (img.url) {
                                    return img.url;
                                }

                                // Se è una stringa URL
                                if (typeof img === 'string') {
                                    return img;
                                }

                                return '';
                            };

                            // Ottieni l'URL dell'immagine
                            const imageUrl = getImageUrl(imageToShow);

                            if (!imageUrl) {
                                console.warn('PREVIEW - Could not determine image URL for', imageToShow);
                                return null;
                            }

                            // Mostra l'immagine
                            return (
                                <img
                                    src={imageUrl}
                                    alt={`Banner ${viewMode}`}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        zIndex: 1,
                                        borderRadius: 'inherit'
                                    }}
                                />
                            );
                        })()}
                        {/* Contenitore flessibile per logo e nome */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '20px',
                            zIndex: 2,
                            width: formData.banner?.logoPosition === 'center' ? 'auto' : '100%',
                            justifyContent: formData.banner?.logoPosition === 'center' ? 'center' :
                                formData.banner?.logoPosition === 'right' ? 'flex-end' : 'flex-start'
                        }}>
                            {/* Logo - Mostra solo se isLogoVisible è true */}
                            {(formData.banner?.isLogoVisible ?? true) && formData.banner?.logo && (
                                <div style={{
                                    position: 'relative',
                                    maxWidth: '120px',
                                    maxHeight: '60px',
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src={getSafeImageUrl(formData.banner?.logo) || 'https://via.placeholder.com/120x60?text=Logo'}
                                        alt="Logo"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            display: 'block',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}

                            {/* Nome del catalogo - Mostra solo se isTitleVisible è true */}
                            {(formData.banner?.isTitleVisible ?? true) && (
                                <div style={{
                                    color: '#fff',
                                    fontSize: viewMode === 'desktop' ? '24px' : '18px',
                                    fontWeight: 'bold',
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                                }}>
                                    {formData.catalogueName || 'Catalog Name'}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Griglia di prodotti */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: viewMode === 'desktop' ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
                        gap: '16px',
                        padding: viewMode === 'desktop' ? '16px' : '10px',
                        backgroundColor: backgroundColor,
                        borderRadius: `0 0 ${formData.customization?.containerCornerRadius || 8}px ${formData.customization?.containerCornerRadius || 8}px`
                    }}>
                        {sampleProducts.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                customization={{
                                    ...formData.customization,
                                    primaryColor,
                                    secondaryColor,
                                    backgroundColor,
                                    actionButtonColor,
                                    bannerBackgroundColor
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CatalogPreview;
